import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDocumentMeta from "../hooks/useDocumentMeta";
import LoadingOverlay from "../components/common/LoadingOverlay";
import InvitationView from "../invitation/InvitationView";
import { reinitWebflow, revealInvitation } from "../invitation/webflow";
import { trackInvitationOpened } from "../lib/analytics";

// Once-per-invitation-page-load guard for invitation_opened (Browser-owned
// engagement). Module-scoped so it survives React StrictMode's double-invoked
// fetch effect and (for the demo) the dual-language fetch; keyed by event_id.
// See docs/analytics/event-ownership.md §5.
const invitationOpenedFired = new Set();

const API_URL = import.meta.env.VITE_API_URL;
// Only the reserved demo slug honours `?lang=` on the BE. Real events
// ignore it, so we fetch them once and skip the parallel-fetch path.
const DEMO_SLUG = "demo-heirloom";

const RSVP = () => {
  const { i18n } = useTranslation();
  const {
    slug,
    rsvpCode: rsvpCodeParam,
    joinCode: joinCodeParam,
  } = useParams();
  const rsvpCode = rsvpCodeParam || null;
  const joinCode = joinCodeParam || null;
  const navigate = useNavigate();
  // Short language code for the demo invite (`en` | `el`). The BE only
  // honours this for the reserved demo slug — real events ignore it.
  const shortLang = (i18n.resolvedLanguage || i18n.language || "en").startsWith(
    "el",
  )
    ? "el"
    : "en";

  // For the demo invite we fetch BOTH language payloads up front and pick
  // the active one client-side on every render. This way a language toggle
  // swaps the BE-served strings (storyBlocks, scheduleEntries, gift
  // message…) instantly without re-running the fetch — which would otherwise
  // trigger the _Agapae destroy/ready cycle below and strand Webflow's
  // mobile nav overlay. Real events ignore `?lang=` on the BE, so they
  // stay on the single-fetch path.
  const [eventByLang, setEventByLang] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);

  const event = useMemo(() => {
    if (!eventByLang) return null;
    return eventByLang[shortLang] || eventByLang.en || null;
  }, [eventByLang, shortLang]);

  useDocumentMeta({
    titleKey: "meta.title",
    descriptionKey: "meta.description",
    descriptionContext: event?.eventType,
  });

  // invitation_opened (Browser-owned engagement) — once per invitation page load,
  // when the public event has resolved and the invitation is about to render. NOT
  // the join truth (that is the Backend-owned guest_joined). The module-scoped
  // guard keyed by event_id collapses StrictMode's double-invoked fetch and the
  // demo's dual-language fetch into a single emission; the not-found / not-ready
  // redirects never set `event`, so a failed load never emits. `language` is the
  // language at open time. See docs/analytics/event-ownership.md §5.
  useEffect(() => {
    const id = event?.eventId;
    if (!id) return;
    if (invitationOpenedFired.has(id)) return;
    invitationOpenedFired.add(id);
    trackInvitationOpened({
      eventId: id,
      eventType: event.eventType,
      language: shortLang,
    });
  }, [event, shortLang]);

  useEffect(() => {
    const isReady = (data) => {
      const hasPartners = !!(data.partner1FirstName && data.partner2FirstName);
      const hasEventDetails = !!(
        data.name &&
        data.dateISO &&
        data.location &&
        data.venueName &&
        data.churchLocation
      );
      const hasSchedule =
        Array.isArray(data.scheduleEntries) && data.scheduleEntries.length > 0;
      return hasPartners && hasEventDetails && hasSchedule;
    };

    const fetchOne = async (lang) => {
      const res = await fetch(
        `${API_URL}/api/events/${slug}/public?lang=${lang}`,
      );
      return { res, lang };
    };

    const fetchEvent = async () => {
      setIsLoading(true);
      try {
        const isDemo = slug === DEMO_SLUG;
        // Demo: fetch en + el in parallel so the toggle is instant.
        // Non-demo: a single fetch — the BE ignores `?lang=` anyway.
        const responses = isDemo
          ? await Promise.all([fetchOne("en"), fetchOne("el")])
          : [await fetchOne(shortLang)];

        // Use the active-language response (or the first one for non-demo)
        // to decide error/readiness routing.
        const primary =
          responses.find((r) => r.lang === shortLang) || responses[0];
        if (!primary.res.ok) {
          if (primary.res.status === 403) {
            // Retention expired or other access denial
            navigate(`/events/${slug}/not-ready`, { replace: true });
          } else {
            navigate(`/events/${slug}/not-found`, { replace: true });
          }
          return;
        }

        const payloads = await Promise.all(
          responses.map(async ({ res, lang }) => ({
            lang,
            data: res.ok ? await res.json() : null,
          })),
        );
        const primaryPayload = payloads.find(
          (p) => p.lang === primary.lang,
        )?.data;
        if (!primaryPayload) {
          navigate(`/events/${slug}/not-found`, { replace: true });
          return;
        }

        // Readiness check mirrors api/utils/invitation-readiness.mjs.
        // `location` is part of the mandatory event-details set there.
        if (!isReady(primaryPayload)) {
          navigate(`/events/${slug}/not-ready`, { replace: true });
          return;
        }

        const byLang = {};
        for (const { lang, data } of payloads) {
          if (data) byLang[lang] = data;
        }
        setEventByLang(byLang);
      } catch (err) {
        console.error("[RSVP] Failed to fetch event:", err);
        navigate(`/events/${slug}/not-found`, { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
    // shortLang is intentionally NOT a dep: the demo path already fetches
    // both languages up front, and re-fetching on every toggle would
    // trigger the _Agapae destroy/ready cycle below, stranding Webflow's
    // mobile nav overlay in the DOM. For non-demo events the BE ignores
    // `?lang=` regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate]);

  useEffect(() => {
    // Reveal nav and content together after the loading overlay has fully exited
    if (showContent) revealInvitation();
  }, [showContent]);

  useEffect(() => {
    // Re-initialize _Agapae interactions after React finishes rendering DOM updates.
    if (!isLoading) reinitWebflow();
  }, [isLoading, slug]);

  // What only a guest's own link knows: the photo-sharing join link (with the
  // event's join code when the link carries one) and their personal RSVP code.
  // Everything else the invitation draws comes from `event` — see
  // invitation/templates/classic/buildClassicModel.js.
  const links = {
    joinUrl: `${import.meta.env.VITE_APP_URL}/j/${slug}${
      joinCode ? `/c/${encodeURIComponent(joinCode)}` : ""
    }`,
    rsvpCode,
    eventId: event?.eventId || slug,
  };

  return (
    <div className="page-wrapper">
      <LoadingOverlay
        isLoading={isLoading}
        onExitComplete={() => setShowContent(true)}
      />
      <InvitationView event={event} revealed={showContent} links={links} />
    </div>
  );
};

export default RSVP;
